import React, { createContext, useContext, useState } from 'react';

const LeadFormContext = createContext();

export const useLeadForm = () => {
  const context = useContext(LeadFormContext);
  if (!context) {
    throw new Error('useLeadForm must be used within a LeadFormProvider');
  }
  return context;
};

export const LeadFormProvider = ({ children, initialData = null }) => {
  const initialLeadSourceId = initialData?.leadSource?.id || null;

  const [formData, setFormData] = useState(() => {
    if (initialData) {
      return {
        customerType: initialData.businessType || 'B2C',
        countryId: initialData.country?.id || null,
        districtId: initialData.district?.id || null,
        cityId: initialData.city?.id || null,
        administrativeRegionId: initialData.administrativeRegion?.id || null,
        streetId: initialData.street?.id || null,
        buildingHouseId: initialData.buildingHouse?.id || null,
        apt: initialData.apt || '',
        entrance: initialData.entrance || '',
        passportId: initialData.passportId || '',
        passportAuthority: initialData.passportAuthority || '',
        passportGivenDate: initialData.passportGivenDate || '',
        dateOfBirth: initialData.dateOfBirth || '',
        registeredAddress: initialData.registeredAddress || '',
        phoneNumber: initialData.phoneNumber || '',
        firstName: initialData.firstName || '',
        lastName: initialData.lastName || '',
        patronymic: initialData.patronymic || '',
        companyName: initialData.companyName || '',
        description: initialData.description || '',
        webAddress: initialData.webAddress || '',
        numberOfEmployees: initialData.numberOfEmployees || '',
        workflowStatusId: initialData.workflowStatus?.id || null,
        leadSourceId: initialData.leadSource?.id || null,
        productIds: initialData.products?.map((p) => p.id) || [],
        offerIds: initialData.offers?.map((o) => o.id) || [],
        competitorId: initialData.competitor?.id || null,
        tariff: initialData.tariff || '',
        includedServices: initialData.includedServices || '',
        contractEndDate: initialData.contractEndDate || null,
        contactDate: initialData.contactDate || null,
        assigneeUuid: initialData.assignee?.uuid || null,
      };
    }

    return {
      customerType: 'B2C',
      countryId: null,
      districtId: null,
      cityId: null,
      administrativeRegionId: null,
      streetId: null,
      buildingHouseId: null,
      apt: '',
      entrance: '',
      passportId: '',
      passportAuthority: '',
      passportGivenDate: '',
      dateOfBirth: '',
      registeredAddress: '',
      phoneNumber: '',
      firstName: '',
      lastName: '',
      patronymic: '',
      companyName: '',
      description: '',
      webAddress: '',
      numberOfEmployees: '',
      workflowStatusId: null,
      leadSourceId: null,
      productIds: [],
      offerIds: [],
      competitorId: null,
      tariff: '',
      includedServices: '',
      contractEndDate: null,
      contactDate: null,
      assigneeUuid: null,
    };
  });

  const updateFormData = (updates) => {
    // setFormData((prev) => {
    //   let hasChanges = false;

    //   for (let key in updates) {
    //     if (prev[key] !== updates[key]) {
    //       hasChanges = true;
    //       break;
    //     }
    //   }

    //   if (!hasChanges) return prev;

    //   return { ...prev, ...updates };
    // });

    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const resetFormData = () => {
    setFormData({
      customerType: 'B2C',
      countryId: null,
      districtId: null,
      cityId: null,
      administrativeRegionId: null,
      streetId: null,
      buildingHouseId: null,
      apt: '',
      entrance: '',
      passportId: '',
      passportAuthority: '',
      passportGivenDate: '',
      dateOfBirth: '',
      registeredAddress: '',
      phoneNumber: '',
      firstName: '',
      lastName: '',
      patronymic: '',
      companyName: '',
      description: '',
      webAddress: '',
      numberOfEmployees: '',
      workflowStatusId: null,
      leadSourceId: null,
      productIds: [],
      offerIds: [],
      competitorId: null,
      tariff: '',
      includedServices: '',
      contractEndDate: null,
      contactDate: null,
      assigneeUuid: null,
    });
  };

  const formatDate = (date) => {
    if (!date) return undefined;
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date;
    }
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return undefined;
    return dateObj.toISOString().split('T')[0];
  };

  const getLeadPayload = () => {
    const isB2B = formData.customerType === 'B2B';

    const basePayload = {
      workflowStatusId: formData.workflowStatusId,
      ...(formData.leadSourceId !== initialLeadSourceId && {
        leadSourceId: formData.leadSourceId,
      }),
      productIds: formData.productIds.length > 0 ? formData.productIds : [],
      offerIds: formData.offerIds.length > 0 ? formData.offerIds : [],
      competitorId: formData.competitorId,
      tariff: formData.tariff || null,
      includedServices: formData.includedServices || null,
      contractEndDate: formatDate(formData.contractEndDate) || null,
      contactDate: formatDate(formData.contactDate) || null,
      assigneeUuid: formData.assigneeUuid,
      phoneNumber: formData.phoneNumber,
      countryId: formData.countryId,
      districtId: formData.districtId,
      cityId: formData.cityId,
      administrativeRegionId: formData.administrativeRegionId,
      streetId: formData.streetId,
      buildingHouseId: formData.buildingHouseId,
      apt: formData.apt || null,
      entrance: formData.entrance || null,
      description: formData.description || null,
      webAddress: formData.webAddress || null,
      numberOfEmployees: formData.numberOfEmployees || null,
    };

    if (isB2B) {
      return {
        ...basePayload,
        companyName: formData.companyName,
      };
    } else {
      return {
        ...basePayload,
        firstName: formData.firstName,
        lastName: formData.lastName,
        patronymic: formData.patronymic || null,
        passportId: formData.passportId || null,
        passportAuthority: formData.passportAuthority || null,
        passportGivenDate: formData.passportGivenDate || null,
        dateOfBirth: formData.dateOfBirth || null,
        registeredAddress: formData.registeredAddress || null,
      };
    }
  };

  const value = {
    formData,
    updateFormData,
    resetFormData,
    getLeadPayload,
  };

  return <LeadFormContext.Provider value={value}>{children}</LeadFormContext.Provider>;
};
